import { format } from 'date-fns';
import { isArray, startCase } from 'lodash';
import { CaseDto } from 'src/crs/dto/case/case.dto';
import { ReferralDto } from 'src/crs/dto/referral.dto';
import { CaseManager, ReferralManager } from 'src/@types/crs/referral';
import { CodeSystem } from 'src/@nicheaim/fhir-base/mappings/CodeSystem';
import { WrappedValueSet } from 'src/@nicheaim/fhir-base/wrappers/ValueSet';
import { Immunization } from 'src/@nicheaim/fhir-base/mappings/Immunization';
import { CaseContactAttemptResponse } from 'src/crs/case/service/CaseService';
import { WrappedCommunication } from 'src/@nicheaim/fhir-base/wrappers/Communication';
import { getDaysSince, getTimeSince, getTimeSince1 } from '../../../utils/formatTime';
import { WrappedServiceRequest } from 'src/@nicheaim/fhir-base/wrappers/ServiceRequest';
import {
  AssessmentsFormsResponse,
  ReferralContactAttemptResponse,
} from 'src/crs/referral/services/ReferralService';
import { DocumentReference } from 'src/nicheaim-infrastructure/application/adapters/out/repositories/fhir/resources';

interface ValueSetModified {
  system?: string | undefined;
  code?: string | undefined;
  display?: string | undefined;
}

export function convertValueToValueSet(
  value: string,
  valueSet: WrappedValueSet | null
): ValueSetModified | null {
  if (!valueSet?.compose?.include?.[0]?.concept) return null;
  const data = {
    ...valueSet.compose.include[0].concept.find((c) => c.code === value || c.display === value),
    system: valueSet.compose.include[0].system,
  };
  return data ? data : null;
}

export function convertValueToValueSetExpansion(
  value: string,
  valueSet: WrappedValueSet | null
): ValueSetModified | null {
  if (!valueSet?.expansion?.contains) return null;
  const data = {
    ...valueSet.expansion.contains.find((c) => c.code === value || c.display === value),
    system: valueSet.expansion.contains?.[0].system,
  };
  return data ? data : null;
}

export function getCodeSystemFromConcept(
  value: string,
  codeSystem: CodeSystem | null
): ValueSetModified | null {
  if (!codeSystem?.concept) return null;
  const data = {
    ...codeSystem.concept.find((c) => c.code === value || c.display === value),
    system: `${process.env.REACT_APP_FHIR_API_BASE_URL}/CodeSystem/${codeSystem?.id}`,
  };
  return data ? data : null;
}

export function getTableRowDueTo(due: Date, status: String) {
  let statusDue: any = {};
  const countDay = getDaysSince(due) * -1;

  if (status === 'completed') {
    statusDue = {
      backgroundColor: '#28a745e6',
      textColor: 'white',
      textShow: '',
      subStatus: 'ontrack',
    };
  } else if (countDay === 2 && status !== 'completed') {
    statusDue = {
      backgroundColor: '#b69329e6',
      textColor: 'white',
      textShow: '(in 2 days)',
      subStatus: 'twodays',
    };
  } else if (due < new Date() && status !== 'completed') {
    statusDue = {
      backgroundColor: '#dc3545e6',
      textColor: 'white',
      textShow: `(${getTimeSince(due)})`,
      subStatus: 'pastdue',
    };
  } else if (due > new Date() && status !== 'completed') {
    statusDue = {
      backgroundColor: '#28a745e6',
      textColor: 'white',
      textShow: `(${getTimeSince1(due)})`,
      subStatus: 'ontrack',
    };
  } else {
    statusDue = { backgroundColor: '', textColor: 'black', textShow: '', subStatus: 'dontdatedue' };
  }

  return statusDue;
}

export function sortByAttribute(arrayToFilter: any[], attribute: string) {
  return arrayToFilter.sort((x1, x2) =>
    x1?.[attribute] > x2?.[attribute] ? 1 : x1?.[attribute] < x2?.[attribute] ? -1 : 0
  );
}

export function mapCommunicationsToNotesDisplay(
  communicationList: WrappedCommunication[],
  code?: string[]
) {
  return communicationList
    .filter(
      (e) => code?.length === 0 || code?.some((r) => r === e?.category?.[0]?.coding?.[0]?.code)
    )
    .map((i) => ({
      topic: i?.topic?.coding?.[0]?.display,
      author: i?.sender?.display,
      type: i?.category?.map((x2) => x2.coding?.[0]?.display)?.join(','),
      description: i?.hasMessages() ? i?.getMessages()?.join(' | ') : i?.getNote(),
      date: i?.sent && format(new Date(i?.sent), 'MMM dd, yyyy'),
    }));
}

export function mapAssessmentsForms(assessmentsFormList: AssessmentsFormsResponse[]) {
  return assessmentsFormList.map((i) => ({
    generic_id: i?.generic_id,
    survey_id: i?.survey_id,
    response_id: i?.response_id,
    start_language: i?.start_language,
    survey_type: i?.survey_type,
    submit_date: i?.submit_date && format(new Date(i?.submit_date), 'MMM dd, yyyy'),
    previewassessments: i?.survey_id,
  }));
}

export function mapImmunizationsToDisplay(immunizationList: Immunization[]) {
  return immunizationList.map((i) => ({
    name: i?.vaccineCode?.text,
    doses: i?.protocolApplied?.[0]?.seriesDosesPositiveInt,
    status: i?.status,
    occurrence: i?.occurrenceDateTime && format(new Date(i?.occurrenceDateTime), 'MMM dd, yyyy'),
    lotNumber: i?.lotNumber,
    vaccineCode: i?.vaccineCode?.coding?.[0]?.code,
  }));
}

export function mapCaseToCasesDisplay(cas: CaseDto) {
  const row = {
    status: cas?.workflowStatus,
    assignedTo:
      cas?.workflowOwnedBy && startCase(cas?.workflowOwnedBy.split('@')[0].replace('.', ' ')),
    scope: '',
    date: cas?.createdOn && format(new Date(cas?.createdOn), 'MMM dd, yyyy'),
  };
  return row;
}

export function mapContactAttemptsReferral(contactAttempts: ReferralContactAttemptResponse[]) {
  return contactAttempts
    ?.filter((item) => item?.noteId)
    .map((i, index) => ({
      when:
        i?.referralContactAttempContactOn &&
        format(new Date(i?.referralContactAttempContactOn), 'MMM dd, yyyy'),
      outcome: i?.referralContactAttempOutcome,
      type: i?.noteType,
      nextContact: {
        data:
          i?.referralContactAttempNextContactOn &&
          format(new Date(i?.referralContactAttempNextContactOn), 'MMM dd, yyyy'),
        last: index === contactAttempts?.length - 1 ? true : false,
      },
      notes: i?.noteText,
    }));
}

export function mapContactAttemptsCases(contactAttempts: CaseContactAttemptResponse[]) {
  return contactAttempts
    ?.filter((item) => item?.noteId)
    .map((i, index) => ({
      when:
        i?.caseContactAttempContactOn &&
        format(new Date(i?.caseContactAttempContactOn), 'MMM dd, yyyy'),
      outcome: i?.caseContactAttempOutcome,
      type: i?.noteType,
      nextContact: {
        data:
          i?.caseContactAttempNextContactOn &&
          format(new Date(i?.caseContactAttempNextContactOn), 'MMM dd, yyyy'),
        last: index === contactAttempts?.length - 1 ? true : false,
      },
      notes: i?.noteText,
    }));
}

export function mapCommunicationsToConsentFormDisplay(
  communicationList: WrappedCommunication[],
  code?: string
) {
  return communicationList
    .filter((e) => e.category?.[0].coding?.[0].code === code)
    .map((i) => ({
      medium:
        i?.medium?.length === 2
          ? `${i?.medium?.[0].coding?.[0].display} , ${i?.medium?.[1].coding?.[0].display}`
          : i?.medium?.[0].coding?.[0].display,
      author: i?.sender?.display,
      date: i?.sent && format(new Date(i?.sent), 'MMM dd, yyyy'),
      status: i?.status,
    }));
}

export function getIdentifierDocumentReference(
  type: string,
  documentReference: DocumentReference | null
) {
  return documentReference?.identifier?.find(
    (e) => e.type?.text === type || e.system?.includes(type)
  )?.value;
}

export function getLatitudeAndLongitude(data: any) {
  const latitude = data?.extension?.[0]?.extension?.find(
    (a: any) => a.url === 'latitude'
  )?.valueDecimal;
  const longitude = data?.extension?.[0]?.extension?.find(
    (a: any) => a.url === 'longitude'
  )?.valueDecimal;
  const identifier = data?.extension?.[0]?.extension?.find(
    (a: any) => a.url === 'identifier'
  )?.valueString;

  return { latitude, longitude, identifier };
}

export function mapServiceRequesToReferralSummary(service: WrappedServiceRequest | null) {
  const serviceMap = {
    status: service?.status,
    ownedBy: service?.performer?.find((t) => t.type === 'Practitioner')?.reference,
    createdOn: service?.authoredOn,
  };
  return serviceMap;
}

export const caseDtoToCaseManager = (cas: CaseDto): CaseManager => {
  const row = {
    id: cas.fhirId,
    mrn: cas.indexedsubjectMrn,
    patient: cas.indexedsubjectFullName,
    phone: cas.indexedsubjectPhone,
    email: cas.indexedsubjectEmail,
    birthDate: cas.indexedsubjectDob,
    assignmentType: '',
    scope: cas.workflowScope,
    step: cas.workflowStageName,
    date: cas.createdOn,
    status: cas.workflowStatus,
    statusIndexed: cas.indexedStatus,
    internalNumber: cas.internalNumber,
    subjectDOBFormatted: cas.subjectDOBFormatted,
    subjectAge: cas.subjectAge,
    createdOnFormatted: cas.createdOnFormatted,
    owner: cas.workflowOwnedBy,
    stepId: cas.workflowStageId,
    stepOrder: cas.workflowStageOrder,
  };
  return row;
};

export const referralDtoToReferralManager = (ref: ReferralDto): ReferralManager => {
  const row = {
    id: ref.fhirId,
    mrn: ref.indexedsubjectMrn,
    referral: ref.indexedsubjectName,
    phone: ref.indexedsubjectPhone,
    email: ref.indexedsubjectEmail,
    birthDate: ref.indexedsubjectDob,
    gender: ref.indexedSubjectGender,
    race: ref.indexedSubjectRace,
    demographics: ref.indexedSubjectEthnicity,
    registrationDate: ref.indexedRegistrationDate,
    status: ref.workflowStatus,
    statusIndexed: ref.indexedStatus,
    referringProvider: ref.indexedPerformerName,
    requesterOrganization: ref.indexedRequesterName,
    rejected: '',
    workflowStageName: ref.workflowStageName,
    internalNumber: ref.internalNumber,
    subjectDOBFormatted: ref.subjectDOBFormatted,
    subjectAge: ref.subjectAge,
    createdOnFormatted: ref.createdOnFormatted,
    owner: ref.workflowOwnedBy,
    scope: ref.workflowScope,
    stepId: ref.workflowStageId,
    stepOrder: ref.workflowStageOrder,
  };
  return row;
};

export function getFullAddress(address: any) {
  let fullAddress = '';
  if (address) {
    if (isArray(address)) {
      address = address[0];
    }
    const line = address?.line ? address?.line.join(', ') : '';
    fullAddress = `${line}, ${address?.city}, ${address?.state}, ${address?.postalCode}, ${address?.country}`;
  }
  return fullAddress;
}
